import { Divider, Grid2, Stack, Typography } from '@mui/material';
import type { FieldProps } from 'formik';
import { Field, Form, Formik } from 'formik';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';
import { boolean, object } from 'yup';

import PostRecordAndAttach from '@components/Attachments/PostRecordAndAttach';
import Checkbox from '@components/Checkbox/index';
import ConsentSurvey from '@components/ConsentSurvey';
import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import RoundedButton from '@components/RoundedButton/index';

import {
  DISCLOSURE_AUTH_CONSENT,
  TERRA_CONSENT,
  VALIDIC_CONSENT,
  VITALL_CONSENT,
} from '@constants/consents';
import { isDevelopment } from '@constants/env';
import {
  TERRA_PRIVACY,
  VALIDIC_PRIVACY,
  VALIDIC_TERMS,
  VITALL_PRIVACY,
  VITALL_TERMS,
} from '@constants/external-links';

import {
  useCreateConsent,
  useGetConsent,
  useGetConsents,
  useUpdateConsent,
} from '@hooks/api/consent';
import { useGetUser, useUpdateUserConsent } from '@hooks/api/user';

import { FhirResource } from '@models/fhir-resource';
import {
  getDisclosureAuthConsentTemplate,
  getTerraConsentTemplate,
  getValidicConsentTemplate,
  getVitallConsentTemplate,
} from '@models/fhir-templates/consent';

import { isClient } from '@utils/role';

import { ConsentStatus } from 'generated/types';

import { consentLanguage } from './consentLanguage';

const messages = defineMessages({
  registrationConsents: {
    id: 'consentForm.registrationConsents',
    defaultMessage: 'Registration Consent',
  },
  recordCollectionAndStorage: {
    id: 'consentForm.recordCollectionAndStorage',
    defaultMessage: 'VITALL Record Collection and Storage Consent',
  },
  validicConsent: {
    id: 'consentForm.validicConsent',
    defaultMessage: 'Fitness and Health Devices Consent',
  },
  tocPnConsent: {
    id: 'consentForm.tocPnConsent',
    defaultMessage: 'I accept the Terms and Conditions and Privacy Notice.',
  },
  caslConsent: {
    id: 'consentForm.caslConsent',
    defaultMessage:
      "I consent to receive marketing and promotional materials about VITALL's current and future products and services.",
  },
  validicMessage: {
    id: 'consentForm.validicMessage',
    defaultMessage:
      'I hereby acknowledge and agree that Validic will collect, store, and use my device data from the smart health and wearable devices I connect, according to its Terms of Use and Privacy Policy, and I hereby authorize Validic to securely transmit a copy of those records to VITALL.',
  },
  vitallMessage: {
    id: 'consentForm.vitallMessage',
    defaultMessage:
      'I{hasName, select, true {, <b>{name}</b>,} other {}} consent to VITALL collecting, storing, and processing my Personally Identifiable Information (“PII”) and Personal Health Information (“PHI”) in accordance with its Terms and Conditions and its Privacy Notice to provide its services.',
  },
  vitallTerra: {
    id: 'consentForm.vitallTerra',
    defaultMessage: 'Smart Health Devices Consent',
  },
  terraConsent: {
    id: 'consentForm.terraConsent',
    defaultMessage:
      'I consent to Terra transmitting data to VITALL from smart health and wearable devices I connect, according to its End User Privacy Policy. Terra does not store any data, including your login credentials.',
  },
  terraTitle: {
    id: 'consentForm.terraTitle',
    defaultMessage:
      'VITALL uses Terra to connect your smart health and wearable device data to your profile. The Terra service includes:',
  },
  terraTrust: {
    id: 'consentForm.terraTrust',
    defaultMessage:
      'Trusted & Secure - Your information is handled anonymously with end-to-end encryption.',
  },
  terraPrivacy: {
    id: 'consentForm.terraPrivacy',
    defaultMessage:
      'You can review a list of permissions before granting Terra access to your system.',
  },
  terraMetrics: {
    id: 'consentForm.terraMetrics',
    defaultMessage:
      'Health Metrics- You are providing this app access to health metrics including your heart rate, steps, sleep among others.',
  },
  disclosureAuthDocs: {
    id: 'consentForm.disclosureAuthDocs.title',
    defaultMessage: 'Disclosure Authorization Documents',
  },
  disclosureAuthCaption: {
    id: 'consentForm.disclosureAuthDocs.description',
    defaultMessage:
      'Disclosure authorization documents give VITALL permission to request records on your behalf as well as give your health care providers permission to disclose records to VITALL. Any and all disclosure documents we use will be available here.',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  agree: {
    id: 'consentForm.agree',
    defaultMessage: 'I Agree',
  },
});

interface ConsentFormProps {
  /** Is this component used for the Terra device consent? */
  terra_consent?: boolean;
  validic_consent?: boolean;
  /** Is this component used for the VITALL & user consents in the user profile management section? */
  vitall_toc_poc_consent?: boolean;
  user_id?: string;
}

const ConsentForm: React.FC<ConsentFormProps> = ({
  terra_consent = false,
  validic_consent = false,
  vitall_toc_poc_consent = false,
  user_id,
}) => {
  const { formatMessage } = useIntl();

  const { data: userData, loading: userLoading } = useGetUser({
    id: user_id ?? '',
  });
  const { data: consentsData, loading: consentsLoading } = useGetConsents();

  const [updateConsent, updateConsentLoading] = useUpdateConsent();
  const [createConsent, createConsentLoading] = useCreateConsent();
  const [updateVitallConsent, updateVitallConsentLoading] =
    useUpdateUserConsent();

  const validicConsent = consentsData.find((c) =>
    c.hasCategory(VALIDIC_CONSENT)
  );
  const terraConsent = consentsData.find((c) => c.hasCategory(TERRA_CONSENT));
  const recordConsent = consentsData.find((c) => c.hasCategory(VITALL_CONSENT));
  const disclosureAuthConsent = consentsData.find((c) =>
    c.hasCategory(DISCLOSURE_AUTH_CONSENT)
  );
  const { data: disclosureAuthConsentWithAttachments } = useGetConsent({
    record_id: disclosureAuthConsent?.id ?? '',
    skip: !disclosureAuthConsent?.id,
  });

  const user = userData?.getUserRecord;
  const patient_id = user?.vitall_patient?.patient_id;

  const initialLoading = userLoading || consentsLoading || createConsentLoading;
  const updateLoading =
    updateConsentLoading || updateVitallConsentLoading || createConsentLoading;

  const initialValues: {
    casl_completed: boolean;
    toc_pn_completed: boolean;
    record_collection: boolean;
    validic_consent: boolean;
    terra_consent: boolean;
    submit: string | null;
  } = {
    casl_completed: user?.casl_completed ?? false,
    toc_pn_completed: user?.toc_completed ?? false,
    record_collection: recordConsent?.isActive() ?? false,
    validic_consent: validicConsent?.isActive() ?? false,
    terra_consent: terraConsent?.isActive() ?? false,
    submit: null,
  };

  const ConsentFormSchema = object().shape({
    casl_completed: boolean(),
    toc_pn_completed: boolean()
      .oneOf([true, false])
      .required(formatMessage(messages.required)),
    record_collection: patient_id
      ? boolean()
          .oneOf([true, false])
          .required(formatMessage(messages.required))
      : boolean(),
    validic_consent: boolean(),
    terra_consent: boolean(),
  });

  const createAuthConsent = async () => {
    const response = await createConsent({
      input: {
        ...getDisclosureAuthConsentTemplate(patient_id ?? ''),
      },
      display_success: false,
    });
    const consentRecord = response.data?.postConsentRecord;
    return consentRecord ? new FhirResource(consentRecord) : null;
  };

  return (
    <ContentLoader loading={initialLoading}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            if (
              user?.user_id &&
              (initialValues.toc_pn_completed !== values.toc_pn_completed ||
                initialValues.casl_completed !== values.casl_completed)
            ) {
              await updateVitallConsent({
                input: {
                  user_id: user.user_id,
                  toc_completed: values.toc_pn_completed,
                  pn_completed: values.toc_pn_completed,
                  casl_completed: values.casl_completed,
                },
              });
            }
            if (
              patient_id &&
              initialValues.record_collection !== values.record_collection
            ) {
              if (recordConsent) {
                await updateConsent({
                  input: {
                    consent_id: recordConsent.getId(),
                    consent: {
                      ...recordConsent.getModelInput(),
                      status: values.record_collection
                        ? ConsentStatus.active
                        : ConsentStatus.inactive,
                    },
                  },
                });
              } else {
                await createConsent({
                  input: {
                    ...getVitallConsentTemplate(
                      patient_id,
                      values.record_collection
                    ),
                  },
                });
              }
            }
            if (
              patient_id &&
              initialValues.validic_consent !== values.validic_consent
            ) {
              if (validicConsent) {
                await updateConsent({
                  input: {
                    consent_id: validicConsent.getId(),
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    consent: {
                      ...validicConsent.getModelInput(),
                      status: values.validic_consent
                        ? ConsentStatus.active
                        : ConsentStatus.inactive,
                    },
                  },
                });
              } else {
                await createConsent({
                  input: {
                    ...getValidicConsentTemplate(
                      patient_id,
                      values.validic_consent
                    ),
                  },
                });
              }
            }

            if (
              patient_id &&
              initialValues.terra_consent !== values.terra_consent
            ) {
              if (terraConsent) {
                await updateConsent({
                  input: {
                    consent_id: terraConsent.getId(),
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                    consent: {
                      ...terraConsent.getModelInput(),
                      status: values.terra_consent
                        ? ConsentStatus.active
                        : ConsentStatus.inactive,
                    },
                  },
                });
              } else {
                await createConsent({
                  input: {
                    ...getTerraConsentTemplate(
                      patient_id,
                      values.terra_consent
                    ),
                  },
                });
              }
            }
          } catch (e) {
            if (isDevelopment) {
              // eslint-disable-next-line no-console
              console.error(e);
            }
          } finally {
            setSubmitting(false);
          }
        }}
        validationSchema={ConsentFormSchema}
      >
        {({ errors, isSubmitting }) => {
          return (
            <Form>
              {vitall_toc_poc_consent && (
                <>
                  {/* Registration Consents */}
                  <Typography variant="h6">
                    {formatMessage(messages.registrationConsents)}
                  </Typography>
                  <ContentLoader loading={updateLoading}>
                    <Grid2 container sx={{ paddingLeft: '18px', mb: 3 }}>
                      <Grid2>
                        <ConsentSurvey
                          links={[
                            {
                              text: 'Terms and Conditions',
                              link: VITALL_TERMS,
                            },
                            {
                              text: 'Privacy Notice',
                              link: VITALL_PRIVACY,
                            },
                          ]}
                          name="toc_pn_completed"
                          text={formatMessage(messages.tocPnConsent)}
                        />
                      </Grid2>
                      <Grid2>
                        <Field name="casl_completed" type="checkbox">
                          {({
                            field,
                            meta,
                            form: { handleChange },
                          }: FieldProps<boolean>) => (
                            <Checkbox
                              checked={field.value}
                              error={Boolean(meta.touched ? meta.error : '')}
                              helperText={meta.touched ? meta.error : ''}
                              label={formatMessage(messages.caslConsent)}
                              name={field.name}
                              onChange={handleChange}
                            />
                          )}
                        </Field>
                      </Grid2>
                    </Grid2>
                  </ContentLoader>

                  {/* Record Consents */}
                  {patient_id ? (
                    <>
                      <Typography variant="h6">
                        {formatMessage(messages.recordCollectionAndStorage)}
                      </Typography>
                      <Typography variant="caption">
                        {formatMessage(consentLanguage.mandatoryConsent)}
                      </Typography>
                      <ContentLoader loading={updateLoading}>
                        <ConsentSurvey
                          links={[
                            {
                              text: 'Terms and Conditions',
                              link: VITALL_TERMS,
                            },
                            {
                              text: 'Privacy Notice',
                              link: VITALL_PRIVACY,
                            },
                          ]}
                          name="record_collection"
                          style={{ paddingBottom: '20px' }}
                          text={formatMessage(messages.vitallMessage, {
                            hasName: false,
                          })}
                        />
                      </ContentLoader>
                    </>
                  ) : null}

                  {patient_id ? (
                    <>
                      <Typography variant="h6">
                        {formatMessage(messages.vitallTerra)}
                      </Typography>
                      <ContentLoader loading={updateLoading}>
                        <ConsentSurvey
                          disabled={!isClient(user)}
                          error={
                            errors.terra_consent &&
                            formatMessage(messages.required)
                          }
                          links={[
                            {
                              text: 'End User Privacy Policy',
                              link: TERRA_PRIVACY,
                            },
                          ]}
                          name="terra_consent"
                          text={formatMessage(messages.terraConsent)}
                          validate
                        />
                      </ContentLoader>
                    </>
                  ) : null}
                </>
              )}

              {/* Validic Consents */}
              {patient_id && validic_consent ? (
                <>
                  <Typography variant="h6">
                    {formatMessage(messages.validicConsent)}
                  </Typography>
                  <ContentLoader loading={updateLoading}>
                    <ConsentSurvey
                      links={[
                        {
                          text: 'Terms of Use',
                          link: VALIDIC_TERMS,
                        },
                        {
                          text: 'Privacy Policy',
                          link: VALIDIC_PRIVACY,
                        },
                      ]}
                      name="validic_consent"
                      text={formatMessage(messages.validicMessage)}
                    />
                  </ContentLoader>
                </>
              ) : null}

              {patient_id && terra_consent ? (
                <>
                  <ContentLoader loading={updateLoading}>
                    <Typography variant="h6">
                      {formatMessage(messages.terraTitle)}
                    </Typography>
                    <ul style={{ margin: '10px' }}>
                      <li>
                        <Typography variant="caption">
                          {formatMessage(messages.terraTrust)}
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="caption">
                          {formatMessage(messages.terraPrivacy)}
                        </Typography>
                      </li>
                      <li>
                        <Typography variant="caption">
                          {formatMessage(messages.terraMetrics)}
                        </Typography>
                      </li>
                    </ul>
                    <ConsentSurvey
                      disabled={!isClient(user)}
                      error={
                        errors.terra_consent && formatMessage(messages.required)
                      }
                      links={[
                        {
                          text: 'End User Privacy Policy',
                          link: TERRA_PRIVACY,
                        },
                      ]}
                      name="terra_consent"
                      text={formatMessage(messages.terraConsent)}
                      validate
                    />
                  </ContentLoader>
                </>
              ) : null}

              {patient_id && vitall_toc_poc_consent ? (
                <Grid2 my={2}>
                  <Typography variant="h6">
                    {formatMessage(messages.disclosureAuthDocs)}
                  </Typography>
                  <Typography variant="caption">
                    {formatMessage(messages.disclosureAuthCaption)}
                  </Typography>

                  <PostRecordAndAttach
                    attachments={
                      disclosureAuthConsentWithAttachments?.attachments
                    }
                    onBeforeUpload={createAuthConsent}
                    resource={disclosureAuthConsent?.getResourceId()}
                  />
                </Grid2>
              ) : null}

              {/* Disclaimer */}
              {vitall_toc_poc_consent && (
                <Stack spacing={2}>
                  <Divider />
                  <Typography variant="overline">
                    {formatMessage(consentLanguage.withdrawingConsent)}
                  </Typography>
                  <Typography variant="subtitle2">
                    {formatMessage(consentLanguage.afterTickConsent)}
                  </Typography>
                  <Typography variant="subtitle2">
                    {formatMessage(consentLanguage.withdrawingConsent2)}
                  </Typography>
                </Stack>
              )}

              <RoundedButton
                disabled={isSubmitting || initialLoading || updateLoading}
                type="submit"
              >
                {formatMessage(
                  terra_consent ? messages.agree : consentLanguage.submit
                )}
              </RoundedButton>
            </Form>
          );
        }}
      </Formik>
    </ContentLoader>
  );
};

export default withErrorBoundary(ConsentForm, {
  FallbackComponent: ErrorBoundaryFallback,
});
